import styles from './EmplyeesListStyles';
import {
  Body1,
  Button,
  Select,
  Tooltip,
  useFluent,
  useScrollbarWidth
} from '@fluentui/react-components';
import {
  Card,
  createTableColumn,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData as RowStateBase,
  useTableFeatures,
  useTableSelection
} from '@fluentui/react-components/unstable';
import { FixedSizeList as List } from "react-window";
import React, { useEffect, useRef, useState } from 'react';
import { AppButton, AppErrorToast, AppLoader, appSuccessToast } from '../common';
import {
  IExpectedHoursItem,
  IExpectedHoursListFilters,
  IExpectedHoursTabProps,
} from '../../services/types/expectedHours';
import ExpectedHoursRow from '../businessComponenets/ExpectedHoursRow';
import { importExcelAndSend, fetchLastUpdated } from '../../services/excelImportService';
import { exportWorkItemsXlsx } from '../../services/expectedHoursService';
import { getSettlementSelectableYears, monthNames } from '../../utils/consts';
import usersService from '../../services/users.service';
import type { IUserSelectable } from '../../services/types/users';

interface TableRowData extends RowStateBase<IExpectedHoursItem> {
  selected: boolean;
  appearance: "brand" | "none";
}

const VIRTUAL_ROW_HEIGHT = 48;

function renderHeaderWithTooltip(label: string, content: string | JSX.Element) {
  return (
    <span style={styles.headerTooltipLabel}>
      <span style={styles.headerTooltipText}>{label}</span>
      <Tooltip relationship="description" content={content}>
        <span style={styles.headerTooltipIcon} aria-label={`Informacja: ${label}`}>i</span>
      </Tooltip>
    </span>
  );
}

const absentifyTooltipContent = (
  <div>
    Godziny nieobecności, które pomniejszają rozliczenie godzin usług.
    <br />
    Wliczane typy:
    <br />- wypoczynkowy urlop
    <br />- zwolnienie lekarskie
    <br />- macierzyński urlop
    <br />- ojcowski urlop
    <br />- okolicznościowy urlop
    <br />- bezpłatny urlop
    <br />- dzień wolny za święto
    <br />- oddanie krwi
    <br />- niepełny etat (wszystkie umowy)
    <br />- przerwa w świadczeniu usług (B2B,UZ)
  </div>
);

const realizedToCollectTooltipContent = (
  <div>
    Suma godzin z realizacji nadgodzin „do odbioru” w danym miesiącu (wg daty realizacji).
    <br />
    Te godziny wchodzą też do YouTrack. Dla UoP porównywane są z kolumną „Odebrane nadgodziny/wyjścia”
    przy liczeniu Sumy i Docelowo — uwzględniana jest wyłącznie dodatnia różnica między tymi kolumnami
    (patrz tooltipy przy Sumie i Docelowo).
  </div>
);

const collectedTooltipContent = (
  <div>
    Suma odebranych nadgodzin oraz wyjść w danym miesiącu.
    <br />
    Dla umów B2B i zlecenia wyjścia nie są ujmowane w tej kolumnie
    (są rozliczane przez kolumnę Nadgodziny 100%).
    <br />
    Dla UoP kolumna uczestniczy w porównaniu z „Zrealizowanymi nadgodzinami do odbioru” przy Sumie i Docelowo.
  </div>
);

const totalTooltipContent = (
  <div>
    Rzeczywiście rozliczone godziny usług w miesiącu.
    <br />
    Liczenie: YouTrack + narzut za 150% i 200% (150% = +0,5h za 1h, 200% = +1h za 1h).
    <br />
    Tylko UoP: dodatkowo max(0, odebrane nadgodziny/wyjścia − zrealizowane nadgodziny do odbioru)
    — tylko nadwyżka odebranych nad zrealizowanymi, bez ponownego dodawania pełnej kolumny zrealizowanych.
    <br />
    Dla B2B i zlecenia ten składnik nie wchodzi do Sumy.
  </div>
);

const targetTooltipContent = (
  <div>
    Docelowa liczba godzin możliwych do rozliczenia.
    <br />
    Bazowo: godziny robocze − Absentify + nadgodziny 100% + (nadgodziny 150% × 1,5) + (nadgodziny 200% × 2).
    <br />
    Dla niepełnoetatowców „godziny robocze” to suma zleconych godzin w alokacji zasobów przez wszystkich kierowników,
    a nie wartość z ustawień godzin roboczych w miesiącu.
    <br />
    Tylko UoP: dodatkowo max(0, zrealizowane nadgodziny do odbioru − odebrane nadgodziny/wyjścia)
    — tylko nadwyżka zrealizowanych nad odebranymi; gdy odebrane ≥ zrealizowane, bez dodatku.
    <br />
    Dodatkowo, gdy „Nadgodziny do odebrania” &lt; 0, wynik jest pomniejszany o brakujące godziny.
  </div>
);

const columns = [
  createTableColumn<IExpectedHoursItem>({ columnId: "userName" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "contractType" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "youtrack" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "absentify" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "toCollect" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "collected" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "realizedToCollectOvertime" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout100" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout150" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout200" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "total" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "workingHours" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "target" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "acceptedTotal" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "status" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "actions" }),
];

const ExpectedHoursList: React.FC<IExpectedHoursTabProps> = ({
  tableHeadTxt,
  items,
  isLoading,
  getMoreItems,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  acceptSettlement,
  rejectSettlement,
  filters,
  setFilters
}) => {
  const { targetDocument } = useFluent();
  const scrollbarWidth = useScrollbarWidth({ targetDocument });

  const {
    getRows,
    selection: { toggleRow, isRowSelected },
  } = useTableFeatures(
    {
      columns,
      items,
    },
    [useTableSelection({ selectionMode: "multiselect" })]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Aktualizacja Absentify: brak');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [selectableUsers, setSelectableUsers] = useState<IUserSelectable[]>([]);

  useEffect(() => {
    usersService.selectableUsers()
      .then(setSelectableUsers)
      .catch(() => setSelectableUsers([]));
  }, []);

  const rows: TableRowData[] = getRows((row) => {
    const selected = isRowSelected(row.rowId);
    return {
      ...row,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === " ") {
          e.preventDefault();
          toggleRow(e, row.rowId);
        }
      },
      selected,
      appearance: selected ? "brand" : "none",
    };
  });

  const goBack = () => window.history.back();

  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value);
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const onEmployeeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, employeeId: value || undefined }));
  };

  const onContractTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as IExpectedHoursListFilters["contractType"] | "";
    setFilters((prev) => ({ ...prev, contractType: value || undefined }));
  };

  const onSettlementStatusSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as IExpectedHoursListFilters["settlementStatus"] | "";
    setFilters((prev) => ({ ...prev, settlementStatus: value || undefined }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      await importExcelAndSend(file, selectedYear);

      appSuccessToast("✅ Dane zostały zaimportowane pomyślnie.");

      const updated = await fetchLastUpdated(selectedYear);
      setLastUpdated(`Aktualizacja Absentify: ${updated}`);

      getMoreItems();
    } catch (err: any) {
      AppErrorToast(err.message || "❌ Wystąpił błąd podczas importu pliku.");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    fetchLastUpdated(selectedYear)
      .then((updated) => setLastUpdated(`Aktualizacja Absentify: ${updated}`))
      .catch(() => setLastUpdated('Aktualizacja Absentify: brak danych'));
  }, [selectedMonth, selectedYear]);

  const selectedMonthIndex = monthNames.indexOf(selectedMonth);

  const handleExportXlsx = () => {
    if (selectedMonthIndex < 0) {
      AppErrorToast("Brak poprawnego miesiąca do eksportu.");
      return;
    }
    exportWorkItemsXlsx(selectedYear, selectedMonthIndex + 1, filters, setIsExporting);
  };

  return (
    <Card style={styles.tableContainer}>
      <Body1>
        <Button appearance="primary" onClick={goBack} style={styles.buttonBack}>
          Powrót
        </Button>

        <div style={styles.tableWithFiltersHeader}>
          {tableHeadTxt ? <h3 style={styles.tableHeadTxt}>{tableHeadTxt}</h3> : null}
          <div style={styles.expectedHoursToolbarRow}>
            Typ umowy:
            <Select style={styles.tableFilter} onChange={onContractTypeSelect} value={filters.contractType ?? ""}>
              <option value="">Wszystkie</option>
              <option value="NONE">Brak</option>
              <option value="B2B">Umowa B2B</option>
              <option value="EmploymentContract">Umowa o pracę</option>
              <option value="ContractOfMandate">Umowa zlecenie</option>
              <option value="Intern">Praktykant/Stażysta</option>
            </Select>

            Pracownik:
            <Select style={styles.tableFilter} onChange={onEmployeeSelect} value={filters.employeeId ?? ""}>
              <option value="">Wszyscy</option>
              {selectableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.lastName} {u.firstName}
                </option>
              ))}
            </Select>

            Status:
            <Select style={styles.tableFilter} onChange={onSettlementStatusSelect} value={filters.settlementStatus ?? ""}>
              <option value="">Wszystkie</option>
              <option value="NONE">Brak decyzji</option>
              <option value="Accepted">Zaakceptowany</option>
              <option value="Rejected">Odrzucony</option>
            </Select>

            Miesiąc:
            <Select style={styles.tableFilter} onChange={onMonthSelect} value={selectedMonth}>
              {monthNames.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Select>
            Rok:
            <Select style={styles.tableFilterYear} onChange={onYearSelect} value={selectedYear}>
              {getSettlementSelectableYears().map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Select>

            <span style={{ flexShrink: 0 }}>
              <AppButton
                text="Eksportuj"
                disabled={isExporting || isUploading}
                appearance="secondary"
                onClick={handleExportXlsx}
              />
            </span>

            <span style={styles.absentifyUpdateAction}>
              <AppButton
                text="Zaktualizuj dane z Absentify"
                disabled={isUploading}
                appearance="secondary"
                onClick={handleUploadClick}
              />
              <Tooltip relationship="description" content={lastUpdated}>
                <span style={styles.inlineTooltipIcon} aria-label={lastUpdated}>
                  i
                </span>
              </Tooltip>
            </span>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              style={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
          </div>
        </div>

        <div className="main-table-container">
          <Table
            noNativeElements
            aria-label="Tabela rozliczeń godzin usług"
            aria-rowcount={rows.length}
            style={styles.table}
          >
            <TableHeader>
              <TableRow aria-rowindex={1}>
                <TableHeaderCell>Nazwisko i imię</TableHeaderCell>
                <TableHeaderCell>Typ umowy</TableHeaderCell>
                <TableHeaderCell>YouTrack</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Absentify", absentifyTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Nadgodziny do odebrania</TableHeaderCell>
                <TableHeaderCell>
                  {renderHeaderWithTooltip("Odebrane nadgodziny/wyjścia", collectedTooltipContent)}
                </TableHeaderCell>
                <TableHeaderCell>
                  {renderHeaderWithTooltip("Zrealizowane nadgodziny do odbioru", realizedToCollectTooltipContent)}
                </TableHeaderCell>
                <TableHeaderCell>Nadgodziny 100%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 150%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 200%</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Suma", totalTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Godziny robocze</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Docelowo", targetTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Zaakceptowane godziny</TableHeaderCell>
                <TableHeaderCell style={styles.statusHeaderCell}>Status</TableHeaderCell>
                <TableHeaderCell style={styles.actionsHeaderCell}>Akcje</TableHeaderCell>
                <div role="presentation" style={{ width: scrollbarWidth }} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length <= 0 && !isLoading && (
                <h4 className="table-empty-value">Brak danych</h4>
              )}

              <List
                height={350}
                itemCount={rows.length}
                itemSize={VIRTUAL_ROW_HEIGHT}
                width="100%"
                itemData={rows}
              >
                {({ index, style, data }) => (
                  <ExpectedHoursRow
                    index={index}
                    style={style}
                    data={data}
                    acceptSettlement={acceptSettlement}
                    rejectSettlement={rejectSettlement}
                    canManageActions
                  />
                )}
              </List>

              {isLoading && items.length === 0 && <AppLoader />}
            </TableBody>
          </Table>
        </div>
      </Body1>
    </Card>
  );
};

export default ExpectedHoursList;
